// Route pattern definitions for dynamic routes
// These are regex patterns that will be matched against the current pathname
const routePatterns = [
  // Document Analyzer dynamic routes
  {
    pattern: /^\/document-analyzer\/[^/]+\/options$/,
    config: {
      info: 'Choose your analysis type for this document. Options include Brief Summary, Long Summary, Headnotes, Brief Notes, Ask Questions, and Custom Prompt.',
      doNotShowAgainKey: 'rcinfo_document_analyzer_options',
    }
  },
  {
    pattern: /^\/document-analyzer\/[^/]+\/summary(\/[^/]+)?$/,
    config: {
      info: 'View your document analysis results. You can see the generated summary, copy it, download it, or translate it to different languages.',
      doNotShowAgainKey: 'rcinfo_document_analyzer_summary',
    }
  },
  {
    pattern: /^\/document-analyzer\/[^/]+\/chat$/,
    config: {
      info: 'Ask questions about your document and get AI-powered answers. The chat feature allows you to interact with your document content.',
      doNotShowAgainKey: 'rcinfo_document_analyzer_chat',
    }
  },
  // Document Review dynamic routes
  {
    pattern: /^\/document-review\/[^/]+\/options$/,
    config: {
      info: 'Select a review type for your document. Choose Overall Review for comprehensive analysis or Personalized Review for tailored insights based on your specific situation.',
      doNotShowAgainKey: 'rcinfo_document_review_options',
    }
  },
  {
    pattern: /^\/document-review\/[^/]+\/overall\/results$/,
    config: {
      info: 'View the comprehensive overall review of your document. This includes key insights, potential issues, and recommendations.',
      doNotShowAgainKey: 'rcinfo_document_review_overall_results',
    }
  },
  {
    pattern: /^\/document-review\/[^/]+\/personalized\/questions$/,
    config: {
      info: 'Answer questions about your specific situation to get a personalized review. Your answers help tailor the analysis to your needs.',
      doNotShowAgainKey: 'rcinfo_document_review_personalized_questions',
    }
  },
  {
    pattern: /^\/document-review\/[^/]+\/personalized\/results$/,
    config: {
      info: 'View your personalized document review results. These insights are tailored based on your specific situation and answers.',
      doNotShowAgainKey: 'rcinfo_document_review_personalized_results',
    }
  },
  // Scanned Copy dynamic routes
  {
    pattern: /^\/scanned-copy(\/[^/]+)?\/options$/,
    config: {
      info: 'Configure options for your true copy generation. Select the output format and additional settings.',
      doNotShowAgainKey: 'rcinfo_scanned_copy_options',
    }
  },
  {
    pattern: /^\/scanned-copy(\/[^/]+)?\/preview$/,
    config: {
      info: 'Preview your generated true copy before downloading. You can make adjustments or regenerate if needed.',
      doNotShowAgainKey: 'rcinfo_scanned_copy_preview',
    }
  },
  // Translator dynamic routes
  {
    pattern: /^\/translator(\/[^/]+)?\/options$/,
    config: {
      info: 'Select translation options for your document. Choose the target language and any additional translation preferences.',
      doNotShowAgainKey: 'rcinfo_translator_options',
    }
  },
  {
    pattern: /^\/translator(\/[^/]+)?\/preview$/,
    config: {
      info: 'Preview your translated document. Review the translation and make any necessary adjustments before downloading.',
      doNotShowAgainKey: 'rcinfo_translator_preview',
    }
  },
  // Event Extraction dynamic routes
  {
    pattern: /^\/event-extraction\/[^/]+$/,
    config: {
      info: 'View extracted timeline events from your document. Events are automatically identified and organized chronologically.',
      doNotShowAgainKey: 'rcinfo_event_extraction_results',
    }
  },
  // Audio Transcription routes
  {
    pattern: /^\/audio-transcription\/language$/,
    config: {
      info: 'Select the language of your audio file for accurate transcription.',
      doNotShowAgainKey: 'rcinfo_audio_transcription_language',
    }
  },
  {
    pattern: /^\/audio-transcription\/transcript$/,
    config: {
      info: 'View your transcribed audio. You can copy, edit, or download the transcript.',
      doNotShowAgainKey: 'rcinfo_audio_transcription_transcript',
    }
  },
  // DraftPro dynamic routes
  {
    pattern: /^\/draftpro\/extract-sections$/,
    config: {
      info: 'Extract and configure sections from your document template. Define the structure for your legal draft.',
      doNotShowAgainKey: 'rcinfo_draft_pro_extract',
    }
  },
  {
    pattern: /^\/draftpro\/generate-draft$/,
    config: {
      info: 'Generate your legal draft based on the configured sections and input data.',
      doNotShowAgainKey: 'rcinfo_draft_pro_generate',
    }
  },
  {
    pattern: /^\/draftpro\/[^/]+$/,
    config: {
      info: 'View and edit your generated legal draft. Make modifications and finalize your document.',
      doNotShowAgainKey: 'rcinfo_draft_pro_view',
    }
  },
  // Subscribed Cases dynamic routes
  {
    pattern: /^\/subscribed-cases\/[^/]+$/,
    config: {
      info: 'View detailed information about this subscribed case including case status, hearing dates, and updates.',
      doNotShowAgainKey: 'rcinfo_subscribed_case_detail',
    }
  },
  // Chat with ID
  {
    pattern: /^\/chat\/[^/]+$/,
    config: {
      info: 'Continue your AI chat conversation. View previous messages and ask new questions.',
      doNotShowAgainKey: 'rcinfo_chat_session',
    }
  },
  // Neurolawgical Research with ID
  {
    pattern: /^\/neurolawgical-research\/[^/]+$/,
    config: {
      info: 'Continue your neurological research session. Access previous insights and continue your legal research.',
      doNotShowAgainKey: 'rcinfo_neurological_research_session',
    }
  },
];

/**
 * Get route configuration for a given pathname.
 * First checks for exact match, then tries pattern matching for dynamic routes.
 * @param {string} pathname - The current route pathname
 * @returns {object|null} - The route configuration or null if not found
 */
export function getRouteConfig(pathname) {
  // First, try exact match
  if (rightClickInfoConfig[pathname]) {
    return rightClickInfoConfig[pathname];
  }
  
  // Then, try pattern matching for dynamic routes
  for (const { pattern, config } of routePatterns) {
    if (pattern.test(pathname)) {
      return config;
    }
  }
  
  return null;
}

const rightClickInfoConfig = {
  '/login': {
    info: 'Enter credentials and click login to access your dashboard.',
    tourSteps: [
      {
        target: 'form[onSubmit="handleLoginSubmit"]',
        title: 'Login Form',
        content: 'Enter your email and password here.',
        placement: 'bottom',
      },
      {
        target: 'form[onSubmit="handleLoginSubmit"] button[type="submit"]',
        title: 'Login Button',
        content: 'Click here to log in.',
        placement: 'bottom',
      },
    ],
    doNotShowAgainKey: 'rcinfo_login',
  },
  '/overview': {
    info: 'This is your Overview dashboard. Use the sidebar to navigate, and see your key stats and charts here.',
    tourSteps: [
      {
        target: '.sidebar',
        title: 'Sidebar',
        content: 'Navigate between different sections here.',
        placement: 'right',
      },
      {
        target: '.overview-donut-card',
        title: 'Case Status Distribution',
        content: 'This chart shows the distribution of your resolved and pending cases.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_dashboard',
  },
  '/': {
    info: 'This is your Home dashboard. Explore various powerful tools for legal professionals, including Neurolawgical Research, Drafting, Subscribe Cases, Cause List, Document Analyzer, Document Review, True Type, and Translator.',
    // Use a smaller target for the main content step (the donut chart card)
    tourSteps: [
      {
        target: '.sidebar',
        title: 'Sidebar',
        content: 'Navigate between different sections here.',
        placement: 'right',
      },
      {
        target: '.overview-donut-card',
        title: 'Case Status Distribution',
        content: 'This chart shows the distribution of your resolved and pending cases.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_dashboard',
  },
  // Add more routes/features as needed
  '/legal-vault': {
    info: 'Manage your legal documents here. Upload, organize, and access your files securely.',
    tourSteps: [
      {
        target: '.legal-vault-files-drop-area',
        title: 'Upload Documents',
        content: 'Drag and drop your legal documents here or click to browse.',
        placement: 'bottom',
      },
      {
        target: '.bg-white.shadow.rounded-lg.overflow-hidden',
        title: 'Your Documents',
        content: 'View and manage your uploaded files and folders.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_legal_vault',
  },
  '/document-analyzer': {
    info: 'Analyze your documents for key insights, summaries, and Q&A.',
    tourSteps: [
      {
        target: '.document-analyzer-upload-section',
        title: 'Upload Document for Analysis',
        content: 'Upload a document to get started with analysis.',
        placement: 'bottom',
      },
      {
        target: '.document-analyzer-summary-panel',
        title: 'Analysis Results',
        content: 'View summaries, extract key information, and ask questions about your document here.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_document_analyzer',
  },
  '/neurolawgical-research': {
    info: 'Engage in advanced neurological research with AI-powered chat and insights. Use the sidebar to manage your research sessions and the chat interface to interact with the AI.',
    tourSteps: [
      {
        target: '.neurological-research-sidebar',
        title: 'Research Sessions',
        content: 'Manage and switch between your different neurological research sessions here.',
        placement: 'right',
      },
      {
        target: '.neurological-research-chat-input',
        title: 'Chat Input',
        content: 'Type your research questions or queries here to get AI-powered insights.',
        placement: 'top',
      },
      {
        target: '.neurological-research-message-list',
        title: 'Conversation History',
        content: 'Review your chat history and AI responses for your current research session.',
        placement: 'bottom',
      },
      {
        target: '.neurological-research-action-buttons',
        title: 'Action Buttons',
        content: 'Access additional actions like sharing, saving, or clearing your research session.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_neurological_research',
  },
  '/draftpro': {
    info: 'Automate legal document drafting based on your templates and facts. Upload a template, configure sections, and generate your draft.',
    tourSteps: [
      {
        target: '[data-feature="DraftPro"] header div.bg-gradient-to-r h1',
        title: 'DraftPro Feature',
        content: 'This is the main title for the DraftPro feature.',
        placement: 'bottom',
      },
      {
        target: '[data-feature="DraftPro"] main .w-full.h-full > div > div:nth-child(1) > h2.text-xl.font-semibold',
        title: 'Start a New Draft Section',
        content: 'This section allows you to begin a new legal draft by uploading a document template.',
        placement: 'bottom',
      },
      {
        target: '[data-feature="DraftPro"] main .w-full.h-full > div > div:nth-child(1) > div.border-dashed.border-2.p-6',
        title: 'Upload Document Template',
        content: 'Drag and drop your .docx template here or click to upload.',
        placement: 'bottom',
      },
      {
        target: '[data-feature="DraftPro"] main .w-full.h-full > div > div:nth-child(2)',
        title: 'Document Preview',
        content: 'Your uploaded document content will be rendered here for easy viewing.',
        placement: 'left',
      },
      {
        target: '[data-feature="DraftPro"] main .w-full.h-full > div > div:nth-child(1) > div.flex.justify-end.space-x-4 > button:nth-child(1)',
        title: 'Step 1: Upload File Button',
        content: 'Click here to proceed after uploading your document.',
        placement: 'top',
      },
      {
        target: '[data-feature="DraftPro"] main .w-full.h-full > div > div:nth-child(1) > div.flex.justify-end.space-x-4 > button:nth-child(2)',
        title: 'Step 2: Process & Continue Button',
        content: 'Click here to process your document and continue to the next step.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_draft_pro',
  },
  '/legal-research': {
    info: 'Conduct comprehensive legal research with AI-powered search and analysis.',
    tourSteps: [
      {
        target: '.legal-research-search-form',
        title: 'Search Form',
        content: 'Enter your legal queries to find relevant information.',
        placement: 'bottom',
      },
      {
        target: '.legal-research-results-display',
        title: 'Search Results',
        content: 'View and analyze the results of your legal research.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_legal_research',
  },
  '/judgement-finder': {
    info: 'Find specific judgments using various criteria and semantic search.',
    tourSteps: [
      {
        target: '.judgement-finder-criteria-search-form',
        title: 'Criteria Search',
        content: 'Use this form to search for judgments based on specific criteria.',
        placement: 'bottom',
      },
      {
        target: '.judgement-finder-results',
        title: 'Judgment Results',
        content: 'Browse through the judgments matching your search.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_judgement_finder',
  },
  '/cases': {
    info: 'Subscribe to cases and receive real-time updates and notifications.',
    tourSteps: [
      {
        target: '.bg-white.p-6.md\\:p-8.rounded-xl.shadow-lg.border.border-gray-200',
        title: 'Case Subscription Form',
        content: 'Enter case details to subscribe for updates.',
        placement: 'bottom',
      },
      {
        target: '.bg-white.w-full.p-5.rounded-lg.shadow-md.border.border-gray-200',
        title: 'Your Subscribed Cases',
        content: 'View and manage your subscribed cases here.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_subscribe_cases',
  },
  '/calendar': {
    info: 'This page displays your cause list. You can view and manage your scheduled case hearings here.',
    tourSteps: [
      {
        target: 'body',
        title: 'Calendar Page Overview',
        content: 'This is the main calendar page where you can view and manage your scheduled case hearings.',
        placement: 'center',
      },
      {
        target: '.rbc-calendar',
        title: 'Calendar View',
        content: 'This calendar shows all your scheduled case hearings.',
        placement: 'top',
      },
      {
        target: '.rbc-toolbar',
        title: 'Calendar Navigation',
        content: 'Use these controls to navigate through the calendar by month, week, or day.',
        placement: 'bottom',
      },
    ],
    doNotShowAgainKey: 'rcinfo_calendar',
  },
  '/document-review': {
    info: 'Upload documents for review to scan, understand, and get legal clarity. Drag and drop files or browse to upload.',
    tourSteps: [
      {
        target: '.document-review-upload-area',
        title: 'Document Upload',
        content: 'Drag and drop your PDF, DOCX, or image files here, or click "BROWSE FILE" to select them.',
        placement: 'bottom',
      },
      {
        target: '.document-review-what-is-section',
        title: 'What is Document Review?',
        content: 'Learn more about the Document Review feature and its benefits.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_document_review',
  },
  '/feature-info': {
    info: 'This section provides general information about various features. Explore the different elements to understand their purpose.',
    tourSteps: [
      {
        target: 'body',
        title: 'Feature Information',
        content: 'This area displays information about specific features or unknown areas.',
        placement: 'center',
      },
      {
        target: '.feature-info-title',
        title: 'Feature Title',
        content: 'This is the title of the feature or area being described.',
        placement: 'bottom',
      },
      {
        target: '.feature-info-content',
        title: 'Feature Details',
        content: 'Detailed information and usage instructions for the feature are shown here.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_feature_info',
  },
  '/event-extraction': {
    info: 'Extract and analyze timeline events from documents using AI. Upload a document to get started.',
    tourSteps: [
      {
        target: '.event-extraction-dropzone',
        title: 'Drag & Drop File',
        content: 'Drag and drop your document here or click "BROWSE FILE" to upload.',
        placement: 'bottom',
      },
      {
        target: '.event-extraction-how-it-works',
        title: 'How It Works',
        content: 'Learn about the process of event extraction and how it benefits you.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_event_extraction',
  },
  '/translator': {
    info: 'Translate documents and text into various languages. Upload a document or enter text to translate.',
    tourSteps: [
      {
        target: '.translator-upload-section',
        title: 'Upload Document for Translation',
        content: 'Upload a document (PDF, DOCX, TXT) to translate its content.',
        placement: 'bottom',
      },
      {
        target: '.translator-text-input',
        title: 'Enter Text for Translation',
        content: 'Type or paste text here to get an instant translation.',
        placement: 'top',
      },
      {
        target: '.translator-language-selector',
        title: 'Select Target Language',
        content: 'Choose the language you want to translate your document or text into.',
        placement: 'right',
      },
      {
        target: '.translator-viewer',
        title: 'Translated Content',
        content: 'View the translated document or text here.',
        placement: 'bottom',
      },
    ],
    doNotShowAgainKey: 'rcinfo_translator',
  },
  '/scanned-copy': {
    info: 'Generate true copies of your scanned documents. Upload a scanned document to create a certified true copy.',
    tourSteps: [
      {
        target: '.scanned-copy-upload-area',
        title: 'Upload Scanned Document',
        content: 'Drag and drop your scanned document (PDF, image) here or click to browse.',
        placement: 'bottom',
      },
      {
        target: '.scanned-copy-viewer',
        title: 'Document Viewer',
        content: 'Preview your uploaded scanned document and the generated true copy.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_scanned_copy',
  },
  '/choose-drafting-method': {
    // info: 'Select a drafting method or template to start a new document. You can choose to start with a blank document, use a previous document, or select from various sample templates.',
    // tourSteps: [
    //   {
    //     target: '.blank-document-card',
    //     title: 'Blank Document',
    //     content: 'Start a new legal document from scratch with a blank canvas.',
    //     placement: 'bottom',
    //   },
    //   {
    //     target: '.previous-documents-section',
    //     title: 'Previous Documents',
    //     content: 'Continue working on or reference your recently drafted documents.',
    //     placement: 'top',
    //   },
    //   {
    //     target: '.sample-templates-section',
    //     title: 'Sample Templates',
    //     content: 'Choose from a variety of pre-designed legal templates to quickly start your draft.',
    //     placement: 'top',
    //   },
    // ],
    info: 'Coming Soon',
    tourSteps: [],
    doNotShowAgainKey: 'rcinfo_choose_drafting_method',
  },
 '/linkedin-post-generator': {
    info: 'Generate engaging LinkedIn posts with AI assistance. Fill in the details and let AI craft your professional posts.',
    tourSteps: [
      {
        target: '.linkedin-post-generator-title',
        title: 'LinkedIn Post Generator',
        content: 'This is the main section for generating LinkedIn posts.',
        placement: 'bottom',
      },
      {
        target: '.linkedin-post-topic-input',
        title: 'Post Topic',
        content: 'Enter the main topic or subject for your LinkedIn post here.',
        placement: 'bottom',
      },
      {
        target: '.linkedin-post-tone-select',
        title: 'Tone of Voice',
        content: 'Select the desired tone for your post (e.g., Formal, Casual, Humorous).',
        placement: 'bottom',
      },
      {
        target: '.linkedin-post-word-limit-select',
        title: 'Word Limit Range',
        content: 'Choose the preferred word count range for your generated post.',
        placement: 'bottom',
      },
      {
        target: '.linkedin-post-language-select',
        title: 'Language',
        content: 'Select the language for your LinkedIn post.',
        placement: 'bottom',
      },
      {
        target: '.linkedin-post-generate-button',
        title: 'Generate Post',
        content: 'Click this button to generate your LinkedIn post based on the provided inputs.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_linkedin_post_generator',
  },
  '/profile': {
    info: 'Manage your user profile, view your completion status, and update your personal information.',
    tourSteps: [
      {
        target: '.relative.w-24.h-24.mb-3',
        title: 'Profile Picture',
        content: 'Your profile picture is displayed here. You can update it in your profile settings.',
        placement: 'bottom',
      },
      {
        target: 'h1.text-2xl.font-bold.text-gray-900',
        title: 'User Name',
        content: 'This is your display name. You can edit it in your profile settings.',
        placement: 'bottom',
      },
      {
        target: '.my-8',
        title: 'Profile Completion',
        content: 'Track your profile completion progress here. Complete your profile to unlock full features.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_profile',
  },
  '/wallet': {
    info: 'Manage your wallet, view your balance, and review transaction history. You can also add funds or manage payment methods here.',
    tourSteps: [
      {
        target: '.wallet-balance-display',
        title: 'Current Balance',
        content: 'This section shows your current wallet balance.',
        placement: 'bottom',
      },
      {
        target: '.wallet-transaction-history',
        title: 'Transaction History',
        content: 'View a detailed history of all your transactions.',
        placement: 'top',
      },
      {
        target: '.wallet-add-funds-button',
        title: 'Add Funds',
        content: 'Click here to add funds to your wallet.',
        placement: 'right',
      },
    ],
    doNotShowAgainKey: 'rcinfo_wallet',
  },
  '/feedback': {
    info: 'Provide your valuable feedback to help us improve. You can submit new feedback or review your past submissions.',
    tourSteps: [
      {
        target: '.feedback-form',
        title: 'Feedback Form',
        content: 'Use this form to submit your suggestions, report issues, or share your experience.',
        placement: 'bottom',
      },
      {
        target: '.feedback-history',
        title: 'Previous Feedback',
        content: 'Review the feedback you have submitted in the past.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_feedback',
  },
  '/privacy-policy': {
    info: 'Review our privacy policy to understand how we collect, use, and protect your personal data.',
    tourSteps: [
      {
        target: 'body',
        title: 'Privacy Policy',
        content: 'This page details our privacy policy.',
        placement: 'center',
      },
    ],
    doNotShowAgainKey: 'rcinfo_privacy_policy',
  },
  '/terms-of-service': {
    info: 'Read our terms of service to understand the rules and regulations governing the use of our platform.',
    tourSteps: [
      {
        target: 'body',
        title: 'Terms of Service',
        content: 'This page outlines our terms of service.',
        placement: 'center',
      },
    ],
    doNotShowAgainKey: 'rcinfo_terms_of_service',
  },
  '/disclaimer': {
    info: 'Understand the disclaimers and limitations of liability regarding the use of our services.',
    tourSteps: [
      {
        target: 'body',
        title: 'Disclaimer',
        content: 'This page provides important disclaimers.',
        placement: 'center',
      },
    ],
    doNotShowAgainKey: 'rcinfo_disclaimer',
  },
  '/chat': {
    info: 'Engage in a chat conversation with our AI assistant. Type your questions or messages in the input field and review the responses in the message list.',
    tourSteps: [
      {
        target: '.chat-sidebar',
        title: 'Chat Sidebar',
        content: 'Manage your chat sessions or access chat settings here.',
        placement: 'right',
      },
      {
        target: '.chat-message-list',
        title: 'Message List',
        content: 'View the history of your conversation with the AI assistant.',
        placement: 'bottom',
      },
      {
        target: '.chat-input-field',
        title: 'Chat Input',
        content: 'Type your message or question here to interact with the AI.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_chat',
  },
  '/changelog': {
    info: 'View the latest updates, new features, and improvements to the platform. Stay informed about what\'s new!',
    tourSteps: [
      {
        target: 'body',
        title: 'Changelog Overview',
        content: 'This page lists all the recent changes and updates to the application.',
        placement: 'center',
      },
      {
        target: '.changelog-list',
        title: 'Update List',
        content: 'Browse through the different versions and their respective changes.',
        placement: 'top',
      },
    ],
    doNotShowAgainKey: 'rcinfo_changelog',
  },
  '/settings': {
    info: 'Configure your account settings, manage preferences, and customize your Neurolaw experience.',
    doNotShowAgainKey: 'rcinfo_settings',
  },
  '/notifications': {
    info: 'View and manage all your notifications. Stay updated with important alerts, case updates, and system messages.',
    doNotShowAgainKey: 'rcinfo_notifications',
  },
  '/audio-transcription': {
    info: 'Transcribe audio files to text using AI-powered speech recognition. Upload audio files and get accurate transcriptions.',
    doNotShowAgainKey: 'rcinfo_audio_transcription',
  },
  '/document-drafting': {
    info: 'Create legal documents using AI-assisted drafting. Upload templates, provide context, and generate professional legal drafts.',
    doNotShowAgainKey: 'rcinfo_document_drafting',
  },
  '/case-finder': {
    info: 'Search and find relevant legal cases using advanced search capabilities. Filter by court, date, and other criteria.',
    doNotShowAgainKey: 'rcinfo_case_finder',
  },
  '/org/dashboard': {
    info: 'Manage your organization, view team members, monitor usage, and configure organization-wide settings.',
    doNotShowAgainKey: 'rcinfo_org_dashboard',
  },
  '/pricing': {
    info: 'Explore our pricing plans and subscription options. Choose the plan that best fits your legal practice needs.',
    doNotShowAgainKey: 'rcinfo_pricing',
  },
  '/prompt-guide': {
    info: 'Learn how to write effective prompts to get the best results from Neurolaw AI. Tips, examples, and best practices.',
    doNotShowAgainKey: 'rcinfo_prompt_guide',
  },
};

export default rightClickInfoConfig;
